<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-card-title class="card-header text-left fixed-card-title2">
			<v-row no-gutters>
				<v-col cols="6">
					<v-toolbar-title>{{ $t('add-condition-header') }}</v-toolbar-title>
				</v-col>
				<v-spacer />
				<v-icon color="white" @click="closeDrawer">mdi-close</v-icon>
			</v-row>
		</v-card-title>
		<v-card-text v-if="currentRecord" class="pt-4 pb-0 overflow-y-auto" style="height: calc(100vh - 205px)">
			<v-row dense class="ma-0">
				<v-col>
					<v-select
						:label="$t('type-of-condition')"
						v-model="selectedType"
						:items="conditionsTemplate"
						item-text="groupName"
						return-object
						outlined
						hide-details
						dense
						:menu-props="{ maxHeight: 199, offsetY: true }" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col>
					<v-radio-group v-model="selectedCondition" dense hide-details class="my-0 pa-0">
						<v-data-table
							class="ma-0 pa-0 data-table-outline border-radius0"
							dense
							:height="Math.max(parseInt(pageHeight(-230)), 260)"
							:headers="headers"
							fixed-header
							:no-data-text="$t('select-a-type')"
							:items="selectedType.conditions"
							:items-per-page="-1"
							:footer-props="{ 'items-per-page-text': '' }">
							<template v-slot:item="{ item }">
								<tr class="cursor" @click="selectedCondition = item">
									<td class="text-left font-size12">
										{{ item.conditionName }}
									</td>
									<td class="text-left font-size12">{{ item.description }}</td>
									<td class="text-left font-size12">
										<v-radio :value="item" />
									</td>
								</tr>
							</template>
						</v-data-table>
					</v-radio-group>
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="8">
					<v-select
						:label="$t('severity-of-condition')"
						v-model="selectedSeverity"
						:items="conditionSeverityOptionTemplate"
						item-text="code"
						outlined
						hide-details
						dense
						:menu-props="{ maxHeight: 199, offsetY: true }">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%"> {{ $t(item.code) }}</span>
								</template>
								{{ item.description }}
							</v-tooltip>
						</template>
					</v-select>
				</v-col>
				<v-col cols="4" class="d-flex justify-center">
					<v-checkbox dense hide-details color="primary" :label="$t('primary')" v-model="isPrimary" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col>
					<v-textarea
						outlined
						dense
						no-resize
						rows="6"
						v-model="managementGoals"
						:label="$t('management-goals')"
						hide-details />
				</v-col>
			</v-row>
		</v-card-text>
		<v-card-text
			v-else
			class="pa-4 overflow-y-auto text-center d-flex flex-column justify-center overflow-x-hidden"
			style="height: calc(100vh - 62px)">
			<v-icon color="grey" size="50" class="mb-3"> mdi-alert-circle-outline </v-icon>
			<p class="font-responsive-lg lightgrey">
				{{ $t('select-a-record') }}
			</p>
		</v-card-text>
		<v-container v-if="currentRecord" class="px-4">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<InputTimeFormatter
						:inputValue="timeSpentSeconds"
						@update:inputValue="(val) => (timeSpentSeconds = val)"
						:inputLabel="$t('time-spent')" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="6" order="1">
					<v-btn @click="closeDrawer()" color="secondary" style="color: black" block>{{ $t('cancel') }}</v-btn>
				</v-col>
				<v-tooltip left v-if="!addButtonEnabled">
					<template v-slot:activator="{ on, attrs }">
						<v-col cols="6" v-bind="attrs" v-on="on" order="2">
							<v-btn disabled block>{{ $t('add') }}</v-btn>
						</v-col>
					</template>
					<span>{{ $t('fill-all-fields-warning') }}</span>
				</v-tooltip>
				<v-col cols="6" v-else order="2">
					<v-btn @click="addCondition()" color="dark_purple" style="color: white" block>{{ $t('add') }}</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import InputTimeFormatter from '../Client/components/InputTimeFormatter.vue';
export default {
	components: { InputTimeFormatter },
	data () {
		return {
			isPrimary: false,
			managementGoals: '',
			selectedType: [],
			selectedCondition: null,
			selectedSeverity: null,
			timeSpentSeconds: 0,
			conditionsTemplate: null,
			conditionSeverityOptionTemplate: null,
		};
	},
	computed: {
		...mapState({
			currentRecord: (state) => state.ccmRecords.ccmRecord,
			userData: (state) => state.authentication.userData,
		}),
		...mapGetters({
			pageHeight: 'pageHeight',
		}),
		addButtonEnabled () {
			return (
				this.timeSpentSeconds &&
				this.selectedCondition &&
				this.managementGoals.trim() &&
				this.selectedSeverity &&
				this.timeSpentSeconds <= 7200
			);
		},
		headers () {
			return [
				{
					text: this.$t('condition-name'),
					sortable: false,
				},
				{
					text: this.$t('description'),
					sortable: false,
				},
				{
					text: '',
					sortable: false,
				},
			];
		},
	},
	methods: {
		...mapActions({
			addCCMCondition: 'ccmConditions/addCCMCondition',
			getConditions: 'ccmConditions/getCCMConditionsByPatientId',
			getAllConditionsTemplates: 'ccmRecords/getAllConditionsTemplates',
			getConditionSeverityOption: 'ccmRecords/getConditionSeverityOption',
			storeCCMRecordsByPatientId: 'ccmRecords/storeMinimalCCMRecordsByPatientId',
		}),
		closeDrawer () {
			this.isPrimary = false;
			this.managementGoals = '';
			this.selectedType = [];
			this.selectedCondition = null;
			this.selectedSeverity = null;
			this.timeSpentSeconds = 0;
			this.$store.commit('SET_DETAILS_DRAWER', false);
		},
		async getVerifiersForDomain () {
			await this.$store.dispatch('verifiers/getICD10CodeForDomainSpecialization');
		},
		async addCondition () {
			const body = {
				ccmRecordId: this.currentRecord.id,
				patientId: this.currentRecord.patientId,
				icD10Code: this.selectedCondition.icd10Codes,
				name: `${this.selectedCondition.conditionName} : ${this.selectedCondition.description}`,
				severity: this.selectedSeverity,
				managementGoals: this.managementGoals,
				isPrimary: this.isPrimary,
				staffId: this.userData.id,
				hospitalId: this.currentRecord.hospitalId,
				timeSpentSeconds: this.timeSpentSeconds,
				condtionTimestampUTC: new Date().toISOString(),
				additionalData: '',
			};
			await this.addCCMCondition(body);
			await this.getConditions(this.currentRecord.patientId);
			await this.storeCCMRecordsByPatientId(this.currentRecord.patientId);
			this.closeDrawer();
		},
	},
	async mounted () {
		if (!this.conditionsTemplate) {
			const response = await this.getAllConditionsTemplates();
			this.conditionsTemplate = response.data;
		}
		if (!this.conditionSeverityOptionTemplate) {
			const response = await this.getConditionSeverityOption();
			this.conditionSeverityOptionTemplate = response.data;
		}
	},
};
</script>
